import * as IoC from 'typescript-ioc';

import { IApiClient } from '../../infrastructure/api-client';
import { IConnectionExportTemplate, IGetUsersParams, IUserCreateDTO, IUserDetailsDTO, IUsersDAO, IUsersListDTO, IUserUpdateDTO } from '../users.dao';
import * as USERS_API from '../../constants/api/users-api';
import { IRolesDTO } from '../roles.dao';
import { IAccountDetailsDTO } from '../accounts.dao';
import { IAccountGroupDTO } from '../account-groups.dao';
import { IPaginationParams } from '../shared.dto';

interface IUsersListDTOInternal {
    content: IUserDetailsDTOInternal[];
    totalElements: number;
}

export interface IUserDetailsDTOInternal {
    accounts: IAccountDetailsDTO[];
    roles: IRolesDTO[];
    isSuperuser: boolean;
    isOpenExchangeStaffMember: boolean;
    allAccounts: boolean;
    email: string;
    username: string;
    firstName: string;
    lastName: string;
    accountGroups: IAccountGroupDTO[];
    confirmationEmailPasswordTrigger: boolean;
    active: boolean;
    lockedSystemUser: boolean;
}

class UsersDAO extends IUsersDAO {
    constructor(@IoC.Inject private readonly apiClient: IApiClient) {
        super();
    }

    public async getUsers(params: IGetUsersParams): Promise<IUsersListDTO> {
        const { query = '', accountIds, oeStaffOnly, ...rest } = params;
        const body: IGetUsersParams = rest;

        if (typeof oeStaffOnly === 'boolean') body.oeStaffOnly = oeStaffOnly;
        if (accountIds) body.accountIds = accountIds;

        const { parsedResponse } = await this.apiClient.get<IUsersListDTOInternal>(USERS_API.SEARCH_USERS,
            { value: query, ...body }
        );

        return {
            ...parsedResponse,
            users: parsedResponse.content.map(x => mapInternalUserDetailsDTOToPublicDTO(x)),
            totalElements: parsedResponse.totalElements
        };
    }

    public async createUser({ isAllAccounts, ...userDetails }: IUserCreateDTO): Promise<IUserDetailsDTO> {
        const response = await this.apiClient.post<IUserDetailsDTOInternal>(
            USERS_API.NEW_USER_URL, {
                ...userDetails,
                allAccounts: isAllAccounts,
            }
        );
        return mapInternalUserDetailsDTOToPublicDTO(response.parsedResponse);
    }

    public async getCurrentUser(): Promise<IUserDetailsDTO> {
        const response = await this.apiClient.get<IUserDetailsDTOInternal>(
            USERS_API.GET_CURRENT_USER_URL
        );
        return mapInternalUserDetailsDTOToPublicDTO(response.parsedResponse);
    }

    public async getUser(username: string): Promise<IUserDetailsDTO> {
        const response = await this.apiClient.get<IUserDetailsDTOInternal>(
            USERS_API.GET_USER_URL, {username: username}
        );
        return mapInternalUserDetailsDTOToPublicDTO(response.parsedResponse);
    }

    public async editUser({ isAllAccounts, ...userDetails }: IUserUpdateDTO, username: string): Promise<void> {
        await this.apiClient.put<void>(
            USERS_API.EDIT_USER_URL, {
                ...userDetails,
                allAccounts: isAllAccounts,
                username: username,
            }, {}
        );
    }

    public async deleteUser(username: string): Promise<void> {
        await this.apiClient.delete<void>(
            USERS_API.DELETE_USER_URL, { username: username }
        );
    }

    public async getUsersCSV(): Promise<void> {
        await this.apiClient.getFile(USERS_API.EXPORT_USERS_URL);
    }

    public async getAccountUsers(params: { accountId: number; activeOnly?: boolean }): Promise<IUserDetailsDTO[]> {
       const {parsedResponse} = await this.apiClient.get<IUserDetailsDTO[]>(USERS_API.ACCOUNT_USERS_URL, params);
       return parsedResponse;
    }

    public async getAccountCoordinators(params: IPaginationParams): Promise<IUsersListDTO> {
        const { query = '', ...rest } = params;
        const { parsedResponse } = await this.apiClient.get<IUsersListDTOInternal>(USERS_API.ACCOUNT_COORDINATORS_URL,
            { value: query, ...rest}
        );
        return {
            ...parsedResponse,
            users: parsedResponse.content.map(x => mapInternalUserDetailsDTOToPublicDTO(x)),
            totalElements: parsedResponse.totalElements
        };
    }

    public async searchDeliveryTeamMembers(params: IGetUsersParams): Promise<IUsersListDTO> {
        const { query = '', ...rest } = params;
        const body: IGetUsersParams = rest;

        const { parsedResponse } = await this.apiClient.get<IUsersListDTOInternal>(USERS_API.SEARCH_DELIVERY_TEAM_MEMBERS_URL,
            { value: query, ...body }
        );

        return {
            ...parsedResponse,
            users: parsedResponse.content.map(x => mapInternalUserDetailsDTOToPublicDTO(x)),
            totalElements: parsedResponse.totalElements
        };
    }


    public async getConnectionExportTemplates(): Promise<IConnectionExportTemplate[]> {
        const {parsedResponse} = await this.apiClient.get<IConnectionExportTemplate[]>(USERS_API.EXPORT_CONNECTIONS_TEMPLATE_URL, );
        return parsedResponse;
    }

    public async createConnectionExportTemplate(template: IConnectionExportTemplate): Promise<IConnectionExportTemplate> {
        const {parsedResponse} = await this.apiClient.post<IConnectionExportTemplate>(USERS_API.EXPORT_CONNECTIONS_TEMPLATE_URL, template);
        return parsedResponse;
    }


    public async deleteConnectionExportTemplates(connectionExportTemplateId: number): Promise<void> {
        await this.apiClient.delete<IConnectionExportTemplate>(USERS_API.EDIT_EXPORT_CONNECTIONS_TEMPLATE_URL , {
                connectionExportTemplateId: connectionExportTemplateId
            });
    }

    public async activateUser(username: string, active: boolean): Promise<void> {
        await this.apiClient.put<void>(
            USERS_API.ACTIVATE_USER_URL, {
                username: username,
            },
            {
                uriParams: {
                    activeStatus: active.toString()
                }
            }
        );
    }
}

function mapInternalUserDetailsDTOToPublicDTO(internalUserDTO: IUserDetailsDTOInternal): IUserDetailsDTO {
    const { firstName, lastName, allAccounts, ...rest } = internalUserDTO;

    return {
        ...rest,
        isAllAccounts: allAccounts,
        firstName: firstName,
        lastName: lastName,
    };
}

export {
    UsersDAO,
    mapInternalUserDetailsDTOToPublicDTO
};
